import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ShieldCheck,
  Wallet,
  Lock,
  Send,
  Activity,
  ListChecks,
  Settings,
  CreditCard,
  ArrowRight,
} from "lucide-react";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import StatCard from "../components/agentProgram/StatCard";
import SectionCard from "../components/agentProgram/SectionCard";
import EmptyState from "../components/agentProgram/EmptyState";

const API = process.env.REACT_APP_API_URL;

const money = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const dateText = (value) =>
  value ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-";

const stageLabel = {
  lead_submitted: "Submitted",
  marked_genuine: "Genuine",
  application_accepted: "Accepted",
  disbursed: "Disbursed",
  clawed_back: "Clawed Back",
};

const quickLinks = [
  { to: "/admin/agent-program/leads", label: "Leads", icon: ListChecks },
  { to: "/admin/agent-program/config", label: "Config", icon: Settings },
  { to: "/admin/agent-program/kyc", label: "KYC Verification", icon: AssignmentIndIcon },
  { to: "/admin/agent-program/payout-requests", label: "Payout Requests", icon: CreditCard },
  { to: "/admin/agent-program/wallet", label: "Agent Wallet", icon: Wallet },
];

export default function AgentProgramOverview() {
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${API}/agent-program/overview`, authConfig);
        setOverview(data);
      } catch (err) {
        setError(err?.response?.data?.message || err.message || "Failed to load overview");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const o = overview || {};
  const leadsByStage = o.leadsByStage || {};
  const funnelStages = ["lead_submitted", "marked_genuine", "application_accepted", "disbursed"];

  // Merge recent leads / requests / release batches into one chronological feed.
  const activity = [
    ...(o.recentLeads || []).map((l) => ({ at: l.updatedAt || l.createdAt, kind: "lead", data: l })),
    ...(o.recentRequests || []).map((r) => ({ at: r.requestedAt, kind: "request", data: r })),
    ...(o.recentReleaseBatches || []).map((b) => ({ at: b.releasedAt, kind: "release", data: b })),
  ]
    .sort((a, b) => new Date(b.at) - new Date(a.at))
    .slice(0, 12);

  return (
    <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-5 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
          <LayoutDashboard size={21} className="text-indigo-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Agent Program Overview</h1>
          <p className="text-sm text-gray-500 mt-0.5">How the whole program is doing, at a glance.</p>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-4">{error}</div>}

      {loading && !overview ? (
        <EmptyState icon={Activity} title="Loading overview…" />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <StatCard title="Agents in Program" value={o.totalAgents ?? 0} icon={Users} tone="bg-indigo-50 text-indigo-600" />
            <StatCard
              title="KYC Verified"
              value={`${o.kycVerifiedAgents ?? 0} / ${o.totalAgents ?? 0}`}
              helper="payout-eligible agents"
              icon={ShieldCheck}
              tone="bg-emerald-50 text-emerald-600"
            />
            <StatCard title="Available Balance" value={`₹${money(o.totalAvailableBalance)}`} helper="owed, withdrawable now" icon={Wallet} tone="bg-blue-50 text-blue-600" />
            <StatCard title="Held Balance" value={`₹${money(o.totalHeldBalance)}`} helper="maturing over 6 months" icon={Lock} tone="bg-amber-50 text-amber-600" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5">
            <div className="lg:col-span-2">
              <SectionCard icon={ListChecks} title="Lead Funnel" subtitle="Every lead currently at each stage">
                <div className="flex items-center flex-wrap gap-2">
                  {funnelStages.map((stage, i) => (
                    <React.Fragment key={stage}>
                      <div className="flex-1 min-w-[90px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-center">
                        <div className="text-lg font-semibold text-gray-900">{leadsByStage[stage] || 0}</div>
                        <div className="text-[11px] text-gray-500 uppercase tracking-wide">{stageLabel[stage]}</div>
                      </div>
                      {i < funnelStages.length - 1 && <ArrowRight size={16} className="text-gray-300 flex-shrink-0" />}
                    </React.Fragment>
                  ))}
                </div>
                {leadsByStage.clawed_back > 0 && (
                  <p className="text-xs text-red-500 mt-3">{leadsByStage.clawed_back} lead(s) clawed back</p>
                )}
              </SectionCard>
            </div>
            <SectionCard icon={Send} title="Pending Payout Requests" subtitle="Agents waiting on admin">
              <Link
                to="/admin/agent-program/payout-requests"
                className="flex items-center justify-between group"
              >
                <span className="text-2xl font-semibold text-gray-900">{o.recentRequests?.filter((r) => r.status === "pending").length ?? 0}</span>
                <span className="text-xs text-blue-600 group-hover:underline flex items-center gap-1">
                  View all <ArrowRight size={12} />
                </span>
              </Link>
            </SectionCard>
          </div>

          <SectionCard icon={Activity} title="Recent Activity" subtitle="Latest leads, requests, and releases across every agent">
            {activity.length === 0 ? (
              <EmptyState icon={Activity} title="No activity yet" compact />
            ) : (
              <div className="divide-y divide-gray-100 -mx-6">
                {activity.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-3 px-6 py-3">
                    {item.kind === "lead" && (
                      <div className="min-w-0 text-sm text-gray-700">
                        <span className="font-medium">{item.data.agent?.name || "Agent"}</span>'s lead{" "}
                        <span className="text-gray-500">{item.data.customerName}</span> is now{" "}
                        <span className="font-medium">{stageLabel[item.data.stage] || item.data.stage}</span>
                      </div>
                    )}
                    {item.kind === "request" && (
                      <div className="min-w-0 text-sm text-gray-700">
                        <span className="font-medium">{item.data.agent?.name || "Agent"}</span> requested ₹{money(item.data.requestedAmount)}
                      </div>
                    )}
                    {item.kind === "release" && (
                      <div className="min-w-0 text-sm text-gray-700">
                        <span className="font-medium">{item.data.agent?.name || "Agent"}</span> was paid ₹{money(item.data.netAmount)}
                      </div>
                    )}
                    <span className="text-xs text-gray-400 flex-shrink-0">{dateText(item.at)}</span>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-5">
            {quickLinks.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col items-center gap-2 text-center hover:border-gray-300 hover:shadow transition-all"
              >
                <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Icon size={17} className="text-gray-500" />
                </div>
                <span className="text-xs font-medium text-gray-700">{label}</span>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
